import { ColDef } from "ag-grid-community";
import {
  SetupMode,
  RowData,
  RowDataReducerState,
  ActiveAccountsReceivableRecordState,
} from "../../../../types";
import { isNewRecord } from "../../helpers";
import { getIsRecordChanged } from "../../validations";
import { HeaderFeatureFlags } from "./landingPageHeader.flags";

interface HeaderVisibilityParams {
  isLoading: boolean;
  isRecordOpen: boolean;
  isReadOnly: boolean;
  setupMode: SetupMode;
  columnDefs: ColDef[];
  rowData: RowData;
  rowDataList: RowData[];
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
  flags: HeaderFeatureFlags;
  isSetupActionsLocked: boolean;
  isSetupSelected: boolean;
  activeAccountsReceivableRecordMigrationInfo?: ActiveAccountsReceivableRecordState;
}

export interface HeaderVisibility {
  // Landing page
  shouldShowLandingPageActions: boolean;
  isNewEntryDisabled: boolean;

  // Record page
  shouldShowRecordPageActions: boolean;
  shouldShowRecordBackToHomeButton: boolean;
  shouldShowCancelOrCloseButton: boolean;
  shouldShowSaveButton: boolean;
  shouldShowSaveAllButton: boolean;
  isClearChangesButtonVisible: boolean;

  // Save state
  isSaveDisabled: boolean;
  isRecordChanged: boolean;

  // Dialogs
  isARMigrationDialogOpen: boolean;
}

export function resolveHeaderVisibility({
  isLoading,
  isRecordOpen,
  isReadOnly,
  setupMode,
  columnDefs,
  rowData,
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  rowDataReducerState,
  flags,
  isSetupActionsLocked,
  isSetupSelected,
  activeAccountsReceivableRecordMigrationInfo,
}: HeaderVisibilityParams): HeaderVisibility {
  const isRecordChanged = getIsRecordChanged({
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
  });

  const isDashboardLevelPrivlibSetup =
    setupMode === "DASHBOARD_LEVEL_SETUP" && isSetupActionsLocked;

  return {
    // Landing page
    shouldShowLandingPageActions:
      !isLoading &&
      isSetupSelected &&
      (!isRecordOpen || setupMode === "INVENTORY_BUILDINGS_SETUP"),
    isNewEntryDisabled:
      isSetupActionsLocked &&
      !(columnDefs.length > 0 && setupMode.includes("DASHBOARD")),

    // Record page
    shouldShowRecordPageActions:
      !isLoading &&
      isSetupSelected &&
      (isRecordOpen || setupMode === "SINGLE_RECORD_DASHBOARD_LEVEL_SETUP"),
    shouldShowRecordBackToHomeButton: isRecordOpen,
    shouldShowCancelOrCloseButton:
      !isDashboardLevelPrivlibSetup &&
      setupMode !== "SINGLE_RECORD_DASHBOARD_LEVEL_SETUP",
    shouldShowSaveButton: !isReadOnly,
    shouldShowSaveAllButton: !isReadOnly && !!selectedTabSectionKey,
    isClearChangesButtonVisible:
      flags.isFormResetFeatureFlagEnabled && !isReadOnly,

    // Save state
    isRecordChanged,
    isSaveDisabled:
      flags.isSaveOnChangeForExistingRecordsFlagEnabled &&
      !isNewRecord(rowData) &&
      !isRecordChanged,
    isARMigrationDialogOpen:
      activeAccountsReceivableRecordMigrationInfo?.isMigrationDialogOpen ??
      false,
  };
}
